import { useCallback, useEffect, useMemo, useState } from 'react';
import { DefaultRootState, useDispatch, useSelector } from 'react-redux';
import { generatePath, useHistory } from 'react-router';

import { CustomerOrderDto, OrderAction } from 'common/api/autogenerated/trading-gateway';

import { useBowser, useI18n, usePopup } from 'common/hooks';
import { CancelOrderPopup, EditLimitOrderPopup } from 'common/popups';

import { actionDeleteOrder, actionGetCustomerOrders } from 'components/Orders/list/redux/orders.actions';

import { routes } from 'routes/routes.constants';

export const useSpotOrders = (
  filterFn: (order: CustomerOrderDto) => boolean = () => true,
  waitFn = false,
  useCached = false,
) => {
  const dispatch = useDispatch();
  const { show, hide } = usePopup();
  const { translate } = useI18n();
  const { isMobile } = useBowser();
  const { push } = useHistory();
  const [initiallyWasLoaded, setInitiallyWasLoaded] = useState(false);

  const { isAuthenticated } = useSelector((state: DefaultRootState) => state.common.user);

  const loading = useSelector((state: DefaultRootState) => state.ordersReducer.loading);
  const orders = (useSelector((state: DefaultRootState) => state.ordersReducer.orders) || []).filter(filterFn);
  const cancel = useCallback((id: number, symbol?: string) => dispatch(actionDeleteOrder(id, symbol)), [dispatch]);
  const reload = useCallback(() => dispatch(actionGetCustomerOrders()), [dispatch]);
  const onEditOrder = useCallback(
    (id: number, action?: OrderAction, price?: number, onCancel?: () => void) => {
      if (isMobile) {
        return new Promise<void>((resolve) => {
          resolve();
          push(generatePath(routes.getEditLimitOrderPage(), { id, action, price }));
        });
      }
      const orderDirectionLabel =
        action === OrderAction.BUY
          ? translate.toText('mobile:asset:order:buy')
          : translate.toText('mobile:asset:order:sell');

      return show(
        EditLimitOrderPopup({
          title: translate
            .toText('mobile:editlimitorder:title')
            .replace('{DIRECTION}', orderDirectionLabel.toLowerCase()),
          orderId: id ?? 0,
          price,
          onCancel,
        }),
      );
    },
    [isMobile, push, show, translate],
  );

  const onConfirmCancelOrder = useCallback(
    (order: CustomerOrderDto) =>
      show(
        CancelOrderPopup({
          order,
          onConfirm: () => cancel(Number(order.id)),
          onCancel: hide,
        }),
      ),
    [cancel, hide, show],
  );

  const userNotAuthenticated = useMemo(() => !isAuthenticated, [isAuthenticated]);
  const isWaitingToProvideFilterFn = useMemo(() => waitFn && !filterFn, [filterFn, waitFn]);
  const existsCached = useMemo(() => useCached && !!orders?.length, [orders?.length, useCached]);

  useEffect(() => {
    if (userNotAuthenticated || isWaitingToProvideFilterFn || initiallyWasLoaded || existsCached) return;
    dispatch(actionGetCustomerOrders());
    setInitiallyWasLoaded(true);
  }, [dispatch, existsCached, initiallyWasLoaded, isWaitingToProvideFilterFn, userNotAuthenticated]);

  return { orders, cancel, loading, reload, onEditOrder, onConfirmCancelOrder };
};
